import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Sidebar } from './Navigation/Sidebar'
import { Topbar } from './Navigation/Topbar'
import { WheelNav } from './Navigation/WheelNav'
import { ToastContainer } from './ui/Toast'
import { Softphone } from './Phone/Softphone'
import { ActiveCallPill } from './Phone/ActiveCallPill'
import { useMinimizePhoneOnNavigate } from './Phone/useMinimizePhoneOnNavigate'
// AC-36: top-of-app soft-cap banner when tenant usage >= 80%.
import { UsageCapBanner } from './Billing/UsageCapBanner'
import GlobalSearchModal from '@/components/Search/GlobalSearchModal'
import { useInboxStats } from '@/hooks/useApi'
import { useUIStore } from '@/stores/uiStore'
import { OfflineBanner } from './OfflineBanner'
import { ErrorBoundary } from './ErrorBoundary'
import { AssistantPanel, useAssistantShortcut } from './Assistant/AssistantPanel'
import { useAssistantStore } from '@/stores/assistantStore'
import { useCallStore } from '@/stores/callStore'

function InboxBadgeSync() {
  const { data } = useInboxStats()
  const setInboxBadge = useUIStore((s) => s.setInboxBadge)
  useEffect(() => {
    if (data?.unreadCount != null) setInboxBadge(data.unreadCount)
  }, [data?.unreadCount, setInboxBadge])
  return null
}

/**
 * Global search (⌘K / Ctrl K). Mounted once here rather than inside the
 * phone header so it works at every width — the sidebar and the phone header
 * both open it through the store.
 */
function GlobalSearch() {
  const searchOpen = useUIStore((s) => s.searchOpen)
  const setSearchOpen = useUIStore((s) => s.setSearchOpen)
  const toggleSearch = useUIStore((s) => s.toggleSearch)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        toggleSearch()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggleSearch])

  return <GlobalSearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
}

/**
 * The in-app assistant (⌘J / Ctrl J). It shares the dock beside the sidebar
 * with the phone, so the phone opening from anywhere (wheel, incoming call)
 * closes it.
 */
function Assistant() {
  useAssistantShortcut()
  const phoneOpen = useCallStore((s) => s.softphoneExpanded)
  useEffect(() => {
    if (phoneOpen) useAssistantStore.getState().setOpen(false)
  }, [phoneOpen])
  return <AssistantPanel />
}

interface LayoutProps {
  children: React.ReactNode
}

/**
 * Publish the height of the in-flow banner stack as `--page-banner-h`.
 *
 * Pages that size a pane off the viewport (Inbox's message list, the calendar
 * grid) subtract a hard-coded chrome constant from 100dvh. Those constants
 * predate the banners, so every banner that appears pushes the pane that many
 * pixels past the bottom of the screen and the whole page starts scrolling.
 * Measuring here means the constant only has to describe fixed chrome, and
 * anything conditional is accounted for by the variable.
 */
function BannerStack({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const publish = () => {
      document.documentElement.style.setProperty('--page-banner-h', `${el.offsetHeight}px`)
    }
    publish()
    // Banners mount, unmount, and reflow on rotate/resize — a one-shot
    // measurement would go stale the first time one of them appeared.
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.documentElement.style.removeProperty('--page-banner-h')
    }
  }, [])

  // `display: flow-root` is load-bearing, not styling. Without it the wrapper
  // has no block formatting context, so a child's top margin collapses
  // *through* it: `UsageCapBanner` is `mt-3`, and those 12px escaped
  // `offsetHeight` entirely. The variable then under-reported the banner by
  // 12px and the pane it feeds stayed 12px too tall — the same overflow, just
  // smaller.
  return <div ref={ref} style={{ display: 'flow-root' }}>{children}</div>
}

// Tab order drives slide direction: moving right along the dock slides the
// new page in from the right, moving left from the left.
const TAB_ORDER = ['/work', '/calendar', '/dashboard', '/inbox']
function sectionOf(path: string) {
  return '/' + (path.split('/')[1] ?? '')
}

/**
 * Screen swap between sections. Remounts per section only (so /work →
 * /work/:id keeps page state), which replays the page's own
 * `animate-fade-up` — the ONE page entrance (UX-098). The wrapper used to run
 * a second 320ms slide/blur on top of the page's fade, so every route change
 * took ~1s to settle; `.page-enter` is now inert and kept only as a hook.
 */
function PageTransition({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const section = sectionOf(pathname)
  const prev = useRef(section)
  const dir = useRef(0)
  if (prev.current !== section) {
    const a = TAB_ORDER.indexOf(prev.current)
    const b = TAB_ORDER.indexOf(section)
    dir.current = a >= 0 && b >= 0 ? Math.sign(b - a) : 0
    prev.current = section
  }
  return (
    <div
      key={section}
      className="page-enter"
      style={{ ['--page-dir' as string]: dir.current }}
    >
      {children}
    </div>
  )
}

export function Layout({ children }: LayoutProps) {
  // Sidebar width is consumed by descendants via the --sidebar-w CSS var
  // (e.g. desktop kanban ultrawide break-out, so it doesn't slide under
  // the sidebar). Sidebar widths are w-16 (64px) collapsed and w-64
  // (256px) expanded. Mobile (<768px) hides the sidebar entirely; the
  // ultrawide break-out that consumes this only fires at ≥1600px, so
  // we can safely assume desktop here.
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const sidebarWidth = sidebarCollapsed ? 64 : 256
  useMinimizePhoneOnNavigate()
  const { pathname } = useLocation()

  return (
    <div
      className="safe-area-layout app-viewport flex overflow-x-clip"
      style={{ ['--sidebar-w' as string]: `${sidebarWidth}px` }}
    >
      {/* Skip link — first tab stop on every page (UX-205). */}
      <a href="#main" className="skip-link">Skip to content</a>
      {/* MIDNIGHT: pure rgb(var(--bg-rgb)) canvas — aurora divs removed */}
      <InboxBadgeSync />

      {/* Sidebar — desktop only */}
      <Sidebar />

      {/* Main area */}
      <div className="app-content-column relative z-10 flex min-h-0 flex-col flex-1 min-w-0">
        <div className="relative z-30 shrink-0">
          <Topbar />
        </div>
        {/* Page content — on phones the Topbar is position:fixed, so add matching top padding.
            From md up there is no Topbar (the sidebar carries its controls).
            Reads --safe-top from index.css to avoid hand-rolling env() (CLAUDE.md §8). */}
        <main
          id="main"
          tabIndex={-1}
          className={`main-scroll-area min-h-0 flex-1 overflow-y-auto pt-[calc(3.5rem+var(--safe-top))] md:pt-[var(--safe-top)] focus:outline-none${/^\/(reports|marketing)(\/|$)/.test(pathname) ? ' has-wheel-clearance' : ''}${pathname.startsWith('/customers') ? ' fab-list-clear' : ''}${pathname === '/payments/tools' ? ' billing-tools-scroll' : ''}`}
        >
          <BannerStack>
            {/* Real network loss only; in flow so it never covers the header. */}
            <OfflineBanner />
            {/* AC-36: soft-cap banner — renders only when >= 80% */}
            <UsageCapBanner />
          </BannerStack>
          <PageTransition>{children}</PageTransition>
        </main>
      </div>

      {/* Phone navigation: one bubble bottom-right that pops out a wheel of
          destinations (and Create). From md up the sidebar is the navigation. */}
      <WheelNav />

      {/* Toast notifications */}
      <ToastContainer />

      {/* Floating widgets get their own boundaries: a crash in one used to
          reach the app-root boundary and replace the whole app. Now it
          just disappears, the page keeps working, and the next page change
          brings it back. */}
      <ErrorBoundary fallback={null} resetKeys={[pathname]}>
        <GlobalSearch />
      </ErrorBoundary>

      {/* Softphone widget — always visible (mounted outside route tree
          so the Twilio Device + active call survive navigation). */}
      <ErrorBoundary fallback={null} resetKeys={[pathname]}>
        <Softphone />

        {/* iOS-style "tap to return to call" pill — visible on every route
            while a call is in progress and the softphone is collapsed. */}
        <ActiveCallPill />
      </ErrorBoundary>

      <ErrorBoundary fallback={null} resetKeys={[pathname]}>
        <Assistant />
      </ErrorBoundary>
    </div>
  )
}
